import { isSSR } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import type { DirectLabCheckoutSource } from "../../hooks/useAnalyticsLogger/DirectLabCheckoutAnalyticsTypes";

const PRODUCT_LINK_PATH_PATTERN = /\/(products?|catalog|labs\/(test|panel))\//i;

/**
 * Best-effort mapping from `utm_source` / `document.referrer` to the
 * `direct_lab_checkout.*` taxonomy's `checkout_source` enum (BUT-1115):
 * `storefront_link` | `product_link` | `email` | `other`.
 *
 * There's no existing signal in this flow for how a visitor actually arrived
 * (see BUT-1219, filed to confirm this mapping with product/analytics before
 * the parent project ships) — this heuristic is a placeholder until that's
 * confirmed. An off-site referrer (search engine, social, another domain
 * entirely) is reported as `other` rather than `storefront_link`, since only
 * a same-host referrer actually tells us the visitor came from within
 * Fullscript's own storefront/product pages.
 */
const resolveDirectLabCheckoutSource = (): DirectLabCheckoutSource => {
  if (isSSR()) return "other";

  const utmSource = new URLSearchParams(window.location.search).get("utm_source")?.toLowerCase();
  if (utmSource === "email") return "email";

  const { referrer } = document;
  if (!referrer) return "other";

  try {
    const referrerUrl = new URL(referrer);
    if (referrerUrl.host !== window.location.host) return "other";

    return PRODUCT_LINK_PATH_PATTERN.test(referrerUrl.pathname)
      ? "product_link"
      : "storefront_link";
  } catch {
    return "other";
  }
};

export { resolveDirectLabCheckoutSource };
